/**
 * FarmRoadAutomationPanel
 *
 * Composant complet pour gérer l'automatisation quotidienne:
 * - Configuration activation/horaire
 * - Déclenchement manuel
 * - Historique des exécutions
 * - Dashboard de supervision (disponibilité données)
 * - Alertes en temps réel
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Switch,
  Button,
  CircularProgress,
  Alert,
  Chip,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Card,
  CardContent,
  LinearProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
  FormControl,
  FormLabel,
  FormControlLabel,
  useMediaQuery,
  useTheme
} from '@mui/material';
import {
  PlayArrow,
  Refresh,
  CheckCircle,
  Error,
  Warning,
  Schedule,
  BarChart,
  Info,
  TrendingUp,
  AccessTime,
  Visibility,
  Close,
  WbSunny,
  Replay,
  History
} from '@mui/icons-material';

import FarmRoadAutomationService from './FarmRoadAutomationService';

const FarmRoadAutomationPanel = ({ farmId, farmName }) => {
  // =========================================================================
  // Responsive
  // =========================================================================
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // =========================================================================
  // État
  // =========================================================================
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [history, setHistory] = useState([]);
  const [monitoringReport, setMonitoringReport] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [stats, setStats] = useState(null);

  const [executing, setExecuting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Modal détails exécution
  const [selectedExecution, setSelectedExecution] = useState(null);
  const [showExecutionDetail, setShowExecutionDetail] = useState(false);

  // Modal relance exécution
  const [rerunExecution, setRerunExecution] = useState(null);
  const [showRerunDialog, setShowRerunDialog] = useState(false);
  const [rerunMode, setRerunMode] = useState('full');

  // =========================================================================
  // Chargement initial
  // =========================================================================
  useEffect(() => {
    if (farmId) {
      loadAutomationData();
    }
  }, [farmId]);

  const loadAutomationData = async () => {
    setLoading(true);
    setError(null);

    try {
      const [configData, statusData, historyData, monitoringData, alertsData, statsData] = await Promise.all([
        FarmRoadAutomationService.getAutomationConfig(farmId),
        FarmRoadAutomationService.getStatus(farmId),
        FarmRoadAutomationService.getExecutionHistory(farmId, { limit: 10 }),
        FarmRoadAutomationService.getMonitoringReport(farmId).catch(() => null),
        FarmRoadAutomationService.getActiveAlerts(farmId).catch(() => []),
        FarmRoadAutomationService.getStats(farmId).catch(() => null)
      ]);

      setConfig(configData);
      setStatus(statusData);
      setHistory(historyData.data || []);
      setMonitoringReport(monitoringData);
      setAlerts(alertsData || []);
      setStats(statsData);
    } catch (err) {
      console.error('Erreur lors du chargement des données d\'automatisation:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // =========================================================================
  // Actions
  // =========================================================================
  const handleToggleAutomation = async () => {
    try {
      const newEnabled = !config.enabled;
      await FarmRoadAutomationService.toggleAutomation(farmId, newEnabled);

      setConfig({ ...config, enabled: newEnabled });
      setSuccess(`Automatisation ${newEnabled ? 'activée' : 'désactivée'}`);

      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleTriggerFullRoutine = async () => {
    setExecuting(true);
    setError(null);

    try {
      const result = await FarmRoadAutomationService.triggerFullRoutine(farmId);
      setSuccess('Routine complète exécutée avec succès !');

      // Recharger les données
      await loadAutomationData();

      setTimeout(() => setSuccess(null), 5000);
    } catch (err) {
      setError(err.message);
    } finally {
      setExecuting(false);
    }
  };

  const handleTriggerRefresh = async () => {
    setExecuting(true);
    setError(null);

    try {
      await FarmRoadAutomationService.triggerDataRefresh(farmId);
      setSuccess('Rafraîchissement des données terminé');
      await loadAutomationData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setExecuting(false);
    }
  };

  const handleTriggerAnalysis = async () => {
    setExecuting(true);
    setError(null);

    try {
      await FarmRoadAutomationService.triggerAIAnalysis(farmId);
      setSuccess('Analyse IA générée avec succès');
      await loadAutomationData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setExecuting(false);
    }
  };

  const handleAcknowledgeAlert = async (alertId) => {
    try {
      await FarmRoadAutomationService.acknowledgeAlert(alertId);
      setAlerts(alerts.filter(a => a.id !== alertId));
      setSuccess('Alerte marquée comme lue');
      setTimeout(() => setSuccess(null), 2000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleTriggerWeatherRefresh = async () => {
    setExecuting(true);
    setError(null);

    try {
      const result = await FarmRoadAutomationService.triggerWeatherRefresh(farmId);
      setSuccess(`Météo rafraîchie : ${result.data.forecast.forecastsCount} prévisions, ${result.data.aggregation.dataPoints} pts historiques`);
      await loadAutomationData();
      setTimeout(() => setSuccess(null), 4000);
    } catch (err) {
      setError(err.message);
    } finally {
      setExecuting(false);
    }
  };

  const handleTriggerWeatherForecast = async () => {
    setExecuting(true);
    setError(null);

    try {
      const result = await FarmRoadAutomationService.triggerWeatherForecast(farmId);
      setSuccess(`Prévisions météo récupérées : ${result.data.forecastsCount} prévisions`);
      await loadAutomationData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setExecuting(false);
    }
  };

  const handleTriggerWeatherAggregation = async () => {
    setExecuting(true);
    setError(null);

    try {
      const result = await FarmRoadAutomationService.triggerWeatherAggregation(farmId);
      setSuccess(`Agrégation météo : ${result.data.dataPoints} pts, qualité ${result.data.quality}`);
      await loadAutomationData();
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setExecuting(false);
    }
  };

  const handleRerun = async () => {
    if (!rerunExecution) return;

    setExecuting(true);
    setError(null);

    try {
      // Calculer la date cible à partir de target_date ou start_time
      const targetDate = rerunExecution.target_date ||
                        new Date(rerunExecution.start_time).toISOString().split('T')[0];

      let result;

      switch (rerunMode) {
        case 'full':
          result = await FarmRoadAutomationService.triggerFullRoutine(farmId, { targetDate });
          setSuccess(`Routine complète relancée pour le ${targetDate}`);
          break;

        case 'weather':
          result = await FarmRoadAutomationService.triggerWeatherRefresh(farmId, { targetDate });
          setSuccess(`Météo relancée pour le ${targetDate}`);
          break;

        case 'analysis':
          result = await FarmRoadAutomationService.triggerAIAnalysis(farmId, { targetDate });
          setSuccess(`Analyse IA relancée pour le ${targetDate}`);
          break;
      }

      // Fermer le dialog et recharger les données
      setShowRerunDialog(false);
      setRerunExecution(null);
      setRerunMode('full');
      await loadAutomationData();

      setTimeout(() => setSuccess(null), 5000);

    } catch (err) {
      setError(err.message);
    } finally {
      setExecuting(false);
    }
  };

  // =========================================================================
  // Helpers de rendu
  // =========================================================================
  const getStatusIcon = (status) => {
    switch (status) {
      case 'success': return <CheckCircle sx={{ color: 'success.main' }} />;
      case 'failed': return <Error sx={{ color: 'error.main' }} />;
      case 'running': return <CircularProgress size={20} />;
      case 'partial': return <Warning sx={{ color: 'warning.main' }} />;
      default: return <Info sx={{ color: 'grey.500' }} />;
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'error';
      case 'warning': return 'warning';
      case 'info': return 'info';
      default: return 'default';
    }
  };

  const formatDuration = (ms) => {
    if (!ms) return '-';
    return `${(ms / 1000).toFixed(1)}s`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleString('fr-FR');
  };

  // =========================================================================
  // Rendu
  // =========================================================================
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box className="p-6 space-y-6">
      {/* Messages d'erreur/succès */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {/* En-tête avec activation */}
      <Paper className="p-4">
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Typography variant="h6" gutterBottom>
              ⚡ {isMobile ? 'Automatisation' : 'Automatisation de la ferme'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Rafraîchissement quotidien des données et analyse IA automatique
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={2}>
            <Box textAlign="right">
              <Typography variant="caption" color="text.secondary">
                Horaire programmé
              </Typography>
              <Box display="flex" alignItems="center" gap={1}>
                <Schedule fontSize="small" />
                <Typography variant="body2" fontWeight="bold">
                  {config?.schedule_time || '07:00'} ({config?.timezone || 'Africa/Casablanca'})
                </Typography>
              </Box>
            </Box>

            <Box display="flex" alignItems="center">
              <Typography variant="body2" mr={1}>
                {config?.enabled ? 'Activée' : 'Désactivée'}
              </Typography>
              <Switch
                checked={config?.enabled || false}
                onChange={handleToggleAutomation}
                color="primary"
              />
            </Box>
          </Box>
        </Box>

        {/* Prochaine exécution */}
        {config?.enabled && status?.scheduler?.nextExecution && (
          <Alert severity="info" sx={{ mt: 2 }} icon={<AccessTime />}>
            <strong>Prochaine exécution:</strong> {status.scheduler.nextExecution}
          </Alert>
        )}
      </Paper>

      {/* Cartes de statistiques */}
      {stats && (
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" variant="caption">
                      Total exécutions
                    </Typography>
                    <Typography variant="h4">{stats.total_executions || 0}</Typography>
                  </Box>
                  <BarChart sx={{ fontSize: 40, color: 'primary.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" variant="caption">
                      Succès
                    </Typography>
                    <Typography variant="h4" color="success.main">
                      {stats.successful_executions || 0}
                    </Typography>
                  </Box>
                  <CheckCircle sx={{ fontSize: 40, color: 'success.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" variant="caption">
                      Échecs
                    </Typography>
                    <Typography variant="h4" color="error.main">
                      {stats.failed_executions || 0}
                    </Typography>
                  </Box>
                  <Error sx={{ fontSize: 40, color: 'error.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography color="text.secondary" variant="caption">
                      Durée moyenne
                    </Typography>
                    <Typography variant="h4">
                      {stats.avg_duration_ms ? `${(stats.avg_duration_ms / 1000).toFixed(0)}s` : '-'}
                    </Typography>
                  </Box>
                  <TrendingUp sx={{ fontSize: 40, color: 'info.main' }} />
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Boutons d'action */}
      <Paper className="p-4">
        <Typography variant="h6" gutterBottom>
          Déclenchement manuel
        </Typography>

        <Box display="flex" gap={2} mt={2}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            startIcon={executing ? <CircularProgress size={20} color="inherit" /> : <PlayArrow />}
            onClick={handleTriggerFullRoutine}
            disabled={executing}
          >
            Exécuter routine complète
          </Button>

          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={loadAutomationData}
          >
            Actualiser
          </Button>
        </Box>
      </Paper>

      {/* Alertes actives */}
      {alerts.length > 0 && (
        <Paper className="p-4">
          <Typography variant="h6" gutterBottom>
            Alertes actives ({alerts.length})
          </Typography>

          <Box display="flex" flexDirection="column" gap={1} mt={2}>
            {alerts.map(alert => (
              <Alert
                key={alert.id}
                severity={getSeverityColor(alert.severity)}
                action={
                  <IconButton size="small" onClick={() => handleAcknowledgeAlert(alert.id)}>
                    <Close fontSize="small" />
                  </IconButton>
                }
              >
                <strong>{alert.message}</strong>
                <Typography variant="caption" display="block" mt={0.5}>
                  {formatDate(alert.created_at)}
                </Typography>
              </Alert>
            ))}
          </Box>
        </Paper>
      )}

      {/* Historique des exécutions */}
      <Paper className="p-4">
        <Typography variant="h6" gutterBottom>
          Historique des exécutions
        </Typography>

        <TableContainer sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Statut</TableCell>
                <TableCell align="right">Mesures</TableCell>
                <TableCell align="center">Météo</TableCell>
                <TableCell align="right">Durée</TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {history.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <Typography variant="body2" color="text.secondary">
                      Aucune exécution enregistrée
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                history.map((exec) => {
                  // Déterminer si on doit afficher le bouton "Relancer"
                  const shouldShowRerun =
                    exec.status === 'failed' ||
                    exec.status === 'partial' ||
                    (exec.status === 'success' && exec.weather_quality === 'estimated') ||
                    (exec.status === 'success' && (!exec.measurements_stored || exec.measurements_stored === 0));

                  return (
                    <TableRow key={exec.id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body2">
                            {formatDate(exec.start_time)}
                          </Typography>
                          {exec.target_date && (
                            <Chip
                              label={`Relance: ${exec.target_date}`}
                              size="small"
                              color="warning"
                              icon={<History fontSize="small" />}
                              sx={{ mt: 0.5 }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip label={exec.task_type} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gap={1}>
                          {getStatusIcon(exec.status)}
                          <span>{exec.status}</span>
                        </Box>
                      </TableCell>
                      <TableCell align="right">{exec.measurements_stored || 0}</TableCell>
                      <TableCell align="center">
                        {(() => {
                          // Compter le nombre d'indicateurs météo réussis (max 3)
                          const indicatorsCollected =
                            (exec.weather_current_collected ? 1 : 0) +
                            (exec.weather_forecast_collected ? 1 : 0) +
                            (exec.weather_aggregated ? 1 : 0);

                          // Vérifier si tous les 3 indicateurs sont collectés
                          const allWeatherSuccess = indicatorsCollected === 3;
                          const someWeatherSuccess = indicatorsCollected > 0;

                          // Déterminer la couleur basée sur le succès complet
                          const getWeatherColor = () => {
                            if (allWeatherSuccess) return 'success'; // Vert si les 3 sont OK
                            if (indicatorsCollected === 2) return 'warning'; // Orange si 2/3
                            if (indicatorsCollected === 1) return 'info'; // Bleu si 1/3
                            return 'default'; // Gris si 0/3
                          };

                          const getWeatherIndicator = () => {
                            if (allWeatherSuccess) return ' ✓'; // Tous les 3 OK
                            if (someWeatherSuccess) return ' ⚠'; // Au moins 1 OK
                            return '';
                          };

                          return indicatorsCollected > 0 ? (
                            <Chip
                              label={`${indicatorsCollected}/3${getWeatherIndicator()}`}
                              size="small"
                              color={getWeatherColor()}
                              sx={{ height: 24, fontSize: '0.75rem' }}
                            />
                          ) : (
                            <Typography variant="caption" color="text.secondary">-</Typography>
                          );
                        })()}
                      </TableCell>
                      <TableCell align="right">{formatDuration(exec.duration_ms)}</TableCell>
                      <TableCell align="center">
                        <Box display="flex" gap={0.5} justifyContent="center">
                          <Tooltip title="Voir détails">
                            <IconButton
                              size="small"
                              onClick={() => {
                                setSelectedExecution(exec);
                                setShowExecutionDetail(true);
                              }}
                            >
                              <Visibility fontSize="small" />
                            </IconButton>
                          </Tooltip>

                          {shouldShowRerun && (
                            <Tooltip title="Relancer cette exécution">
                              <IconButton
                                size="small"
                                color="warning"
                                onClick={() => {
                                  setRerunExecution(exec);
                                  setShowRerunDialog(true);
                                }}
                                disabled={executing}
                              >
                                <Replay fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Modal détails exécution */}
      <Dialog
        open={showExecutionDetail}
        onClose={() => setShowExecutionDetail(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Détails de l'exécution
        </DialogTitle>
        <DialogContent>
          {selectedExecution && (
            <Box>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Début</Typography>
                  <Typography variant="body2">{formatDate(selectedExecution.start_time)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Fin</Typography>
                  <Typography variant="body2">{formatDate(selectedExecution.end_time)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Mesures récupérées</Typography>
                  <Typography variant="body2">{selectedExecution.measurements_fetched || 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Mesures stockées</Typography>
                  <Typography variant="body2">{selectedExecution.measurements_stored || 0}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Analyse générée</Typography>
                  <Typography variant="body2">{selectedExecution.analysis_generated ? 'Oui' : 'Non'}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Durée</Typography>
                  <Typography variant="body2">{formatDuration(selectedExecution.duration_ms)}</Typography>
                </Grid>
              </Grid>

              {/* Section Météo - 3 Composantes */}
              {(selectedExecution.weather_current_collected || selectedExecution.weather_forecast_collected || selectedExecution.weather_aggregated) && (
                <Box sx={{ mt: 3, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                  <Typography variant="subtitle2" gutterBottom display="flex" alignItems="center" gap={1}>
                    <WbSunny color="action" />
                    Données météo ({[selectedExecution.weather_current_collected, selectedExecution.weather_forecast_collected, selectedExecution.weather_aggregated].filter(Boolean).length}/3 composantes)
                  </Typography>
                  <Grid container spacing={2} sx={{ mt: 1 }}>
                    {/* 1. Observations actuelles */}
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        1. Observations actuelles (données du jour)
                      </Typography>
                      <Typography variant="body2" display="flex" alignItems="center" gap={1}>
                        {selectedExecution.weather_current_collected ? (
                          <>
                            <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                            Collectées
                          </>
                        ) : (
                          <>
                            <Error sx={{ fontSize: 16, color: 'error.main' }} />
                            Non collectées
                          </>
                        )}
                      </Typography>
                    </Grid>

                    {/* 2. Prévisions */}
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        2. Prévisions météo (5 jours)
                      </Typography>
                      <Typography variant="body2" display="flex" alignItems="center" gap={1}>
                        {selectedExecution.weather_forecast_collected ? (
                          <>
                            <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                            {selectedExecution.weather_forecasts_count || 0} prévisions collectées
                          </>
                        ) : (
                          <>
                            <Error sx={{ fontSize: 16, color: 'error.main' }} />
                            Non collectées
                          </>
                        )}
                      </Typography>
                    </Grid>

                    {/* 3. Agrégat historique */}
                    <Grid item xs={12}>
                      <Typography variant="caption" color="text.secondary" display="block">
                        3. Agrégat historique quotidien
                      </Typography>
                      {selectedExecution.weather_aggregated ? (
                        <Box>
                          <Typography variant="body2" display="flex" alignItems="center" gap={1}>
                            <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                            Complété - {selectedExecution.weather_data_points || 0} points de données
                          </Typography>
                          <Box sx={{ ml: 3, mt: 0.5 }}>
                            <Typography variant="caption" color="text.secondary">
                              Date: {selectedExecution.weather_date || 'N/A'} | Qualité:{' '}
                              <Chip
                                label={selectedExecution.weather_quality || 'N/A'}
                                size="small"
                                color={
                                  selectedExecution.weather_quality === 'complete' ? 'success' :
                                  selectedExecution.weather_quality === 'partial' ? 'warning' : 'default'
                                }
                                sx={{ height: 18, fontSize: '0.65rem' }}
                              />
                            </Typography>
                          </Box>
                        </Box>
                      ) : (
                        <Typography variant="body2" display="flex" alignItems="center" gap={1}>
                          <Error sx={{ fontSize: 16, color: 'error.main' }} />
                          Non disponible
                        </Typography>
                      )}
                    </Grid>
                  </Grid>
                </Box>
              )}

              {selectedExecution.error_message && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {selectedExecution.error_message}
                </Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowExecutionDetail(false)}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog de relance */}
      <Dialog
        open={showRerunDialog}
        onClose={() => setShowRerunDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Relancer l'exécution du {rerunExecution && formatDate(rerunExecution.start_time)}
        </DialogTitle>
        <DialogContent>
          {rerunExecution && (
            <>
              <Alert severity="warning" sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Exécution précédente : <strong>{rerunExecution.status}</strong>
                </Typography>
                <Typography variant="body2">
                  • Mesures : {rerunExecution.measurements_stored || 0} stockées
                </Typography>
                <Typography variant="body2">
                  • Météo : {rerunExecution.weather_quality || 'non collectée'}
                  {rerunExecution.weather_data_points > 0 && ` (${rerunExecution.weather_data_points} points)`}
                </Typography>
                {rerunExecution.error_message && (
                  <Typography variant="body2" color="error">
                    • Erreur : {rerunExecution.error_message}
                  </Typography>
                )}
              </Alert>

              <FormControl fullWidth sx={{ mt: 2 }}>
                <FormLabel>Que souhaitez-vous relancer ?</FormLabel>
                <RadioGroup value={rerunMode} onChange={(e) => setRerunMode(e.target.value)}>
                  <FormControlLabel
                    value="full"
                    control={<Radio />}
                    label="Routine complète (mesures + météo + analyse)"
                  />
                  <FormControlLabel
                    value="weather"
                    control={<Radio />}
                    label="Météo uniquement (agrégation données historiques)"
                    disabled={!rerunExecution.measurements_stored}
                  />
                  <FormControlLabel
                    value="analysis"
                    control={<Radio />}
                    label="Analyse IA uniquement (avec données existantes)"
                    disabled={!rerunExecution.measurements_stored || !rerunExecution.weather_aggregated}
                  />
                </RadioGroup>
              </FormControl>

              <Alert severity="info" sx={{ mt: 2 }}>
                Les données existantes seront écrasées si la relance réussit.
              </Alert>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowRerunDialog(false)}>Annuler</Button>
          <Button
            variant="contained"
            onClick={handleRerun}
            disabled={executing}
            startIcon={executing && <CircularProgress size={16} />}
          >
            {executing ? 'Relance en cours...' : 'Relancer'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FarmRoadAutomationPanel;
